import { cloneMap, DataModel, DataModelBuilder, isSameData,
  isSameMap, mapToData, SerializationContext, SerializedData } from '../dto/dto.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Currency } from '../serializable/currency.js';
import { MonthOfYear } from './month-of-year.js';
import { TimeZoneRegion } from './time-zone-region.js';
import { DailyStatistics } from './daily-statistics.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { VersionRange } from '../core/version-range.js';
import { ICopyWithBusinessRef } from '../../contracts/copy-with-business-ref.js';

export class MonthlyStaffStatistics extends DataModel implements ICopyWithBusinessRef {
  static readonly TYPE_ID: string = 'b84858f679234aa6b10cd5684b848238';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly businessRef: BusinessModuleRef,
    public readonly staffId: string,
    public readonly monthOfYear: MonthOfYear,
    public readonly timezone: TimeZoneRegion,
    public readonly currency: Currency,
    public readonly data: Map<number, DailyStatistics>,
  ) {
    super();
  }

  get $typeId(): string {
    return MonthlyStaffStatistics.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(MonthlyStaffStatistics.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): MonthlyStaffStatistics {
    return new MonthlyStaffStatistics(
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.get<string>(map, 'staffId'),
        context.fromData<MonthOfYear>(MapUtils.getRaw(map, 'monthOfYear') as SerializedData),
        MapUtils.get<TimeZoneRegion>(map, 'timezone'),
        MapUtils.get<Currency>(map, 'currency'),
        MapUtils.getMap<number, DailyStatistics>(map, 'data', (k, v) => {
          return { key: Number(k), value: context.fromData<DailyStatistics>(v as SerializedData) };
        }),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'businessRef': this.businessRef.encode(),
      'staffId': this.staffId,
      'monthOfYear': context.toData(this.monthOfYear),
      'timezone': this.timezone,
      'currency': this.currency,
      'data': mapToData(this.data, (k, v) => {
        return { key: k.toString(), value: context.toData(v) };
      }),
    };
  }

  clone(): this {
    return new MonthlyStaffStatistics(
        this.businessRef.clone(),
        this.staffId,
        this.monthOfYear.clone(),
        this.timezone,
        this.currency,
        cloneMap(this.data),
    ) as this;
  }

  isSame(other: MonthlyStaffStatistics) {
    if(this === other) {
      return true;
    }

    return isSameData(other.businessRef, this.businessRef) &&
      other.staffId === this.staffId &&
      other.monthOfYear.isSame(this.monthOfYear) &&
      other.timezone === this.timezone &&
      other.currency === this.currency &&
      isSameMap(other.data, this.data, isSameData);
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new MonthlyStaffStatistics(
        businessRef.clone(),
        this.staffId,
        this.monthOfYear.clone(),
        this.timezone,
        this.currency,
        cloneMap(this.data),
    ) as this;
  }
}

export class MonthlyStaffStatisticsBuilder extends DataModelBuilder<MonthlyStaffStatistics> {
  build(context: SerializationContext, map: SerializedData): MonthlyStaffStatistics {
    return MonthlyStaffStatistics.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(MonthlyStaffStatistics.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(MonthlyStaffStatistics.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version: number = versionRange.to;

    if(version < 2) {
      MapUtils.set(map, 'businessRef', MapUtils.getOrNull(map, 'businessRef') ?? MapUtils.get(map, 'businessId'));
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
